import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Container, Spinner, EmptyState } from "@/components/common";
import { DanceCard } from "@/components/DanceCard";
import { LevelBadge } from "@/components/LevelBadge";
import { formatTime, formatDateShort } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Calendar, Heart, BookOpen, Sparkles, CalendarDays, MapPin, Clock, ArrowRight, AlertTriangle, ShieldCheck } from "lucide-react";

function Widget({ title, icon: Icon, action, children }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-[#2D1E18]"><Icon size={18} className="text-[#B04B26]" /> {title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function MemberDashboard() {
  const { user, refresh } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/me/dashboard")).data });
  const [promptDismissed, setPromptDismissed] = useState(false);
  if (isLoading) return <Spinner />;

  const showReviewPrompt = user && !user.safety_review_prompted_at && !promptDismissed;
  const dismissPrompt = async () => { setPromptDismissed(true); try { await api.post("/me/dismiss-review-prompt"); refresh(); } catch {} };

  return (
    <Container className="py-8">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-widest text-[#B04B26]">Your dashboard</p>
        <h1 className="mt-1 font-display text-3xl font-extrabold text-[#2D1E18]">Hi {user?.first_name || "there"} 👋</h1>
      </div>

      {showReviewPrompt && (
        <div className="mb-6 rounded-2xl border-2 border-[#C85A32]/30 bg-[#FBEBE4]/60 p-5" data-testid="review-contact-prompt">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <ShieldCheck size={22} className="mt-0.5 shrink-0 text-[#B04B26]" />
              <div>
                <h2 className="font-display text-lg font-bold text-[#2D1E18]">Keep your contact details up to date</h2>
                <p className="mt-0.5 text-sm text-[#5C4A42]">Add a mobile number for emergency SMS alerts and an optional emergency contact. It only takes a minute — and it's entirely optional.</p>
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <Link to="/account" onClick={dismissPrompt} data-testid="review-prompt-go" className="rounded-full bg-[#C85A32] px-4 py-2 text-sm font-bold text-white hover:bg-[#B04B26]">Review now</Link>
              <button onClick={dismissPrompt} data-testid="review-prompt-skip" className="rounded-full border border-border px-4 py-2 text-sm font-semibold text-[#6E5B51] hover:text-[#2D1E18]">Skip for now</button>
            </div>
          </div>
        </div>
      )}

      {data?.announcements?.length > 0 && (
        <div className="mb-6 space-y-2">
          {data.announcements.slice(0, 3).map((a) => (
            <div key={a.id} className={`flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${(a.category === "urgent" || a.priority === "urgent") ? "border-[#EF4444] bg-[#FEF2F2] text-[#991B1B]" : "border-border bg-secondary text-[#5C4A42]"}`}>
              {(a.category === "urgent" || a.priority === "urgent") && <AlertTriangle size={16} className="mt-0.5 shrink-0" />}
              {a.category === "important" && <AlertTriangle size={16} className="mt-0.5 shrink-0 text-[#9A5B1E]" />}
              <p><span className="font-bold">{a.title}</span>{a.message ? ` — ${a.message}` : ""}{a.personal && <span className="ml-2 rounded-full bg-[#E8F0EC] px-2 py-0.5 text-[10px] font-bold uppercase text-[#4A6B5D]">Your class</span>}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Widget title="My Classes" icon={Calendar} action={<Link to="/my-classes" className="text-sm font-semibold text-[#B04B26] hover:underline">Manage</Link>}>
            {data?.my_classes?.length ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {data.my_classes.map((c) => (
                  <Link key={c.id} to={`/classes/${c.slug}`} className="rounded-xl border border-border p-4 hover:border-[#C85A32]">
                    <div className="flex items-center justify-between gap-2"><span className="font-display font-bold text-[#2D1E18]">{c.name}</span><LevelBadge level={c.level} /></div>
                    <p className="mt-1 inline-flex items-center gap-1 text-sm text-[#5C4A42]"><Clock size={13} /> {c.day} {formatTime(c.start_time)}</p>
                    <p className="inline-flex items-center gap-1 text-sm text-[#6E5B51]"><MapPin size={13} /> {c.venue?.name}</p>
                  </Link>
                ))}
              </div>
            ) : <EmptyState title="No classes followed yet" subtitle="Add the classes you attend to personalise your hub."><Link to="/my-classes" className="inline-flex items-center gap-1 rounded-full bg-[#C85A32] px-4 py-2 text-sm font-bold text-white">Add classes <ArrowRight size={14} /></Link></EmptyState>}
          </Widget>

          <Widget title="New dances from your classes" icon={Sparkles} action={<Link to="/dances" className="text-sm font-semibold text-[#B04B26] hover:underline">Library</Link>}>
            {data?.new_dances?.length ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {data.new_dances.slice(0, 4).map((d) => <DanceCard key={d.id} dance={d} compact />)}
              </div>
            ) : <p className="text-sm text-[#6E5B51]">Once your classes post their dances, they'll appear here.</p>}
          </Widget>

          <Widget title="Continue learning" icon={BookOpen} action={<Link to="/my-dances" className="text-sm font-semibold text-[#B04B26] hover:underline">All</Link>}>
            {data?.learning?.length ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{data.learning.slice(0, 4).map((d) => <DanceCard key={d.id} dance={d} compact />)}</div>
            ) : <p className="text-sm text-[#6E5B51]">Mark dances as “Learning” and they'll show up here.</p>}
          </Widget>
        </div>

        <div className="space-y-6">
          <Widget title="My favourites" icon={Heart}>
            {data?.favourites?.length ? (
              <ul className="space-y-2">{data.favourites.map((d) => <li key={d.id}><Link to={`/dances/${d.slug}`} className="flex items-center justify-between rounded-lg px-2 py-2 hover:bg-secondary"><span className="font-semibold text-[#2D1E18]">{d.name}</span><LevelBadge level={d.level} /></Link></li>)}</ul>
            ) : <p className="text-sm text-[#6E5B51]">Tap the heart on any dance to save it here.</p>}
          </Widget>

          <Widget title="Upcoming events" icon={CalendarDays} action={<Link to="/events" className="text-sm font-semibold text-[#B04B26] hover:underline">All</Link>}>
            {data?.events?.length ? (
              <ul className="space-y-3">{data.events.map((e) => <li key={e.id}><Link to={`/events/${e.slug}`} className="block rounded-lg px-2 py-2 hover:bg-secondary"><p className="font-semibold text-[#2D1E18]">{e.title}</p><p className="text-sm text-[#6E5B51]">{formatDateShort(e.date)}{e.venue ? ` · ${e.venue.name}` : ""}</p></Link></li>)}</ul>
            ) : <p className="text-sm text-[#6E5B51]">No events scheduled right now.</p>}
          </Widget>

          <div className="rounded-2xl border border-border bg-secondary p-6">
            <h3 className="font-display font-bold text-[#2D1E18]">Quick links</h3>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <Link to="/whats-on" className="rounded-lg bg-card px-3 py-2 font-semibold text-[#B04B26]">What's on</Link>
              <Link to="/my-dances" className="rounded-lg bg-card px-3 py-2 font-semibold text-[#B04B26]">My dances</Link>
              <Link to="/dances" className="rounded-lg bg-card px-3 py-2 font-semibold text-[#B04B26]">Dance library</Link>
              <Link to="/account" className="rounded-lg bg-card px-3 py-2 font-semibold text-[#B04B26]">Account</Link>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
}
